import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { approverApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import { ArrowLeft, Check, X } from 'lucide-react';
import { visaTypeLabel } from '../../lib/visas.js';

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-border/50 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value || '—'}</span>
    </div>
  );
}

export default function ReviewVisaPage() {
  const { userId, id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [visa, setVisa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    approverApi.getVisa(userId, id)
      .then((res) => setVisa(res.visa))
      .catch((err) => { toast.error(err.message); navigate('/visa-approvals'); })
      .finally(() => setLoading(false));
  }, [userId, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async () => {
    setBusy(true);
    try {
      await approverApi.approveVisa(userId, id, { note });
      toast.success('Approved');
      navigate('/visa-approvals');
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const reject = async () => {
    if (!note.trim()) { toast.error('Please give a reason'); return; }
    setBusy(true);
    try {
      await approverApi.rejectVisa(userId, id, { note });
      toast.success('Rejected');
      navigate('/visa-approvals');
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="h-6 w-6 animate-spin rounded-full border-[2.5px] border-border border-t-primary" /></div>;
  if (!visa) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <button onClick={() => navigate('/visa-approvals')} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to queue
      </button>
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Visa request</h1>
        <p className="mt-1 text-sm text-muted-foreground">{visa.requesterName} · {visa.requesterEmail}</p>
      </div>
      <Card><CardContent className="py-4">
        <Row label="Type" value={visaTypeLabel(visa.visaType)} />
        <Row label="Purpose" value={visa.purpose} />
        <Row label="Needed by" value={visa.neededBy} />
        <Row label="Department" value={visa.requesterDepartment} />
      </CardContent></Card>

      {!rejecting ? (
        <div className="flex items-center gap-2">
          <Button onClick={approve} loading={busy} disabled={busy}><Check className="h-4 w-4" /> Approve</Button>
          <Button variant="secondary" onClick={() => setRejecting(true)} disabled={busy}><X className="h-4 w-4" /> Reject</Button>
        </div>
      ) : (
        <Card><CardContent className="py-4 space-y-3">
          <Input label="Reason for rejection" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Explain what needs to change" />
          <div className="flex items-center gap-2">
            <Button variant="danger" onClick={reject} loading={busy} disabled={busy}>Confirm rejection</Button>
            <Button variant="ghost" onClick={() => { setRejecting(false); setNote(''); }} disabled={busy}>Cancel</Button>
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}
